import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { flushBeforeSignOut, startQueueLoop } from "./features/feedback/queue.ts";
import "./styles.css";

declare global {
  interface Window {
    EXCALIDRAW_ASSET_PATH?: string | string[];
  }
}

/**
 * Шрифты холста Excalidraw грузятся в рантайме и по умолчанию с esm.sh.
 * Переключаем на свой origin до первого рендера: внешних доменов в
 * рендер-пути быть не должно (раздел 4 CLAUDE.md). Файлы кладёт туда
 * плагин edu-excalidraw-assets из vite.config.ts.
 */
window.EXCALIDRAW_ASSET_PATH = "/excalidraw/";

/**
 * Разбор очереди заметок о неточностях (`docs/feedback-notes-design.md`,
 * раздел 6.1).
 *
 * ⚠️ Цикл заводится ЗДЕСЬ, до рендера, а не внутри виджета, и это разница между
 * работающей очередью и очередью, которая не разбирается никогда. Виджет
 * рисуется только при живой сессии, а `lib/session.tsx` переводит состояние в
 * `anon` при ЛЮБОЙ неудаче `getMe()`, включая недоступный API и обрыв сети (там
 * это прямо прокомментировано, разницы для экрана входа нет). То есть цикл,
 * поставленный под условие «есть сессия», при лежащем API не завёлся бы вовсе,
 * и заметки лежали бы в браузере до тех пор, пока API не поднимется И человек
 * не перезагрузит вкладку. А написаны они как раз тогда, когда что-то лежит.
 *
 * По той же причине цикл ничего не знает о React: он переживает и `loading`, и
 * `anon`, и вход, а условием видимости гасится только кнопка.
 */
startQueueLoop();

/**
 * Досылка при закрытии вкладки.
 *
 * ⚠️ `pagehide`, а не кнопка «Выйти», и это единственное место, где нужен
 * `keepalive`: выход это обычный клик внутри SPA, вкладка при нём жива, и
 * доставкой там занимается `flushBeforeSignOut` из `signOut` (см.
 * `lib/session.tsx`). А вот закрытие вкладки обрывает обычный `fetch` на
 * полпути, и заметка, написанная за секунду до закрытия, уехала бы только на
 * следующем открытии приложения, то есть через день.
 *
 * ⚠️ Отсюда виден только повод, а сам флаг ставит очередь: наружу она отдаёт
 * одну досылку, и это правильно, потому что тело запроса собирает она.
 * Требование к ней записано здесь, потому что проверить его отсюда нечем:
 * досылка, идущая на выгрузке вкладки, обязана уходить с `keepalive: true`
 * (`postFeedback` принимает флаг именно для этого случая). Без флага браузер
 * оборвёт запрос, и вся эта строка будет выглядеть работающей, не доставляя
 * ничего.
 *
 * `pagehide`, а не `unload` и не `beforeunload`: на мобильных браузерах вкладку
 * выгружают из фона без них. Тот же выбор и по той же причине сделан в
 * телеметрии комнаты (`features/room/telemetry.ts`).
 *
 * Слушатель не снимается: он живёт ровно столько же, сколько сама вкладка.
 */
window.addEventListener("pagehide", () => {
  void flushBeforeSignOut();
});

const el = document.getElementById("root");
if (!el) throw new Error("#root не найден");

createRoot(el).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
